import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { RUM_TYPE_LABELS, toDisplayScore, type RevealResultView } from '@cercle/shared';
import { useApi } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { useSessionSocket } from '@/shared/ws/useSessionSocket';
import { Avatar, Bottle, EmptyState, Gauge, Kicker, Muted, Skeleton, ui } from '@/shared/ui';
import styles from './blind.module.scss';

/**
 * Écran 06 — la révélation (US-15).
 *
 * Le seul moment où le référentiel autorise un geste appuyé : 1,6 s pendant lesquelles le
 * voile monte, la bouteille reprend ses couleurs et le halo s'ouvre. Partout ailleurs, les
 * mouvements restent « courts et bas ».
 *
 * Le résultat arrive par le socket et n'est pas rechargé en HTTP : tout le monde doit voir
 * la même chose au même instant, un rechargement décalerait les écrans les uns des autres.
 */
export function RevealScreen(): ReactNode {
  const { id: sessionId = '' } = useParams();
  const api = useApi();
  const [live, setLive] = useState<RevealResultView | null>(null);

  useSessionSocket(sessionId, { onRumRevealed: setLive });

  // Filet : si la révélation a eu lieu avant l'ouverture de cet écran, on retrouve le rhum
  // déjà dévoilé dans la séance.
  const session = useAsync(() => api.sessions.byId(sessionId), [sessionId]);

  if (live) return <RevealBody reveal={live} animated />;
  if (session.loading) return <Skeleton lines={4} />;

  const revealed = session.data?.rums.find((slot) => slot.isBlind && slot.revealedAt);
  if (!revealed) return <Waiting />;

  return (
    <RevealBody
      animated={false}
      reveal={{
        sessionId,
        rumId: revealed.rum.id,
        revealedAt: revealed.revealedAt ?? '',
        rum: {
          id: revealed.rum.id,
          name: revealed.rum.name,
          distillery: revealed.rum.distillery,
          country: revealed.rum.country,
          rumType: revealed.rum.rumType,
          abv: revealed.rum.abv,
          age: revealed.rum.age,
          photoUrl: revealed.rum.photoUrl,
        },
        podium: [],
        bestNoseMemberId: null,
        tastings: [],
        average: revealed.rum.averageScore,
      }}
    />
  );
}

/** « La pièce respire lentement, rien d'autre ne bouge à l'écran. » */
function Waiting(): ReactNode {
  return (
    <div className={styles.reveal}>
      <span className={styles.kickerCenter}>Dégustation à l’aveugle</span>
      <div className={styles.waiting}>
        <span className={styles.waitingHalo} aria-hidden="true" />
        <span className={styles.waitingPill}>
          <span className={styles.waitingDot} />
          <strong>En attente de la révélation…</strong>
        </span>
      </div>
      <Muted>L’écran se dévoilera de lui-même quand le fondateur déclenchera.</Muted>
    </div>
  );
}

function RevealBody({
  reveal,
  animated,
}: {
  reveal: RevealResultView;
  animated: boolean;
}): ReactNode {
  const bestNose = reveal.podium.find((entry) => entry.member.id === reveal.bestNoseMemberId);
  const best = reveal.tastings.reduce((max, t) => Math.max(max, t.overall), 1);

  const specs = [
    reveal.rum.country,
    reveal.rum.rumType ? RUM_TYPE_LABELS[reveal.rum.rumType] : null,
    reveal.rum.abv ? `${String(reveal.rum.abv).replace('.', ',')} %` : null,
    reveal.rum.age ? `${reveal.rum.age} ans` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={styles.reveal}>
      {/* Le voile ne se joue qu'à la révélation en direct ; à l'arrivée tardive, la fiche
          est simplement là — on ne rejoue pas un moment qu'on a manqué. */}
      {animated ? <span className={styles.veil}>Rhum mystère</span> : null}

      <span className={styles.kickerCenter}>La révélation</span>
      <p className={styles.cameWas}>C’était…</p>

      <div className={styles.bottleStage}>
        {animated ? <span className={styles.revealHalo} aria-hidden="true" /> : null}
        <Bottle size="lg" />
      </div>

      <h1 className={styles.revealName}>{reveal.rum.name}</h1>
      <p className={styles.revealSpecs}>{specs}</p>

      {bestNose ? (
        <div className={styles.bestNose}>
          {animated ? <span className={styles.bestNoseGlow} aria-hidden="true" /> : null}
          <Avatar pseudo={bestNose.member.pseudo} url={bestNose.member.avatarUrl} />
          <span className={styles.bestNoseText}>
            <Kicker>Meilleur nez</Kicker>
            <strong>{bestNose.member.pseudo}</strong>
          </span>
        </div>
      ) : reveal.podium.length > 0 ? (
        <Muted>Personne ne se détache : pas de meilleur nez ce soir.</Muted>
      ) : null}

      <div className={styles.revealColumns}>
        {reveal.podium.length > 0 ? (
          <section>
            <h2 className={styles.sectionTitle}>Podium des pronostics</h2>
            <ol className={styles.podium}>
              {reveal.podium.map((entry, index) => (
                <li
                  key={entry.member.id}
                  className={ui.row}
                  // Cascade : 90 ms entre chaque rang, du premier au dernier.
                  style={animated ? { animationDelay: `${index * 90}ms` } : undefined}
                >
                  <span className={styles.rank}>{entry.rank}</span>
                  <Avatar pseudo={entry.member.pseudo} url={entry.member.avatarUrl} size="sm" />
                  <span className={styles.podiumInfo}>
                    <strong>{entry.member.pseudo}</strong>
                    <span>{entry.detail}</span>
                  </span>
                  <span className={styles.points}>{entry.score} pts</span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {reveal.tastings.length > 0 ? (
          <section>
            <h2 className={styles.sectionTitle}>
              Notes comparées
              {reveal.average !== null ? (
                <span className={styles.average}> · moyenne {toDisplayScore(reveal.average)}</span>
              ) : null}
            </h2>
            <ul className={styles.notes}>
              {reveal.tastings.map((tasting) => (
                <li key={tasting.member.id}>
                  <span className={styles.noteName}>{tasting.member.pseudo}</span>
                  <span className={styles.noteGauge}>
                    <Gauge percent={(tasting.overall / best) * 100} />
                  </span>
                  <span className={styles.noteValue}>{toDisplayScore(tasting.overall)}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      {reveal.podium.length === 0 && reveal.tastings.length === 0 ? (
        <EmptyState>
          Le détail des pronostics a été diffusé au moment de la révélation.
        </EmptyState>
      ) : null}
    </div>
  );
}
